import React, { Component } from 'react';
import PropTypes from 'prop-types';
import Loading from 'components/common/loading-component';
import ApiModel from 'models/api-model';
import { FormattedMessage } from 'react-intl';
import { connect } from 'react-redux';
import {
  createErrorStackTraceAction,
  createCatchedErrorJsonAction,
} from 'store/actions/api-error-handler-actions';
import * as sourceStackTrace from 'sourcemapped-stacktrace';
import { toast } from 'react-toastify';
import ApiErrorNotification, {
  API_ERROR_NOTIFICATION_ID,
} from 'components/util/api-error-notificiation-component';
import CloseToastButton from 'components/util/close-toast-button-component';
class ConditionalRender extends Component {
  state = { error: null, processedDependencyErrors: new Set() };

  static getDerivedStateFromError(error) {
    return { error };
  }

  componentDidCatch(...args) {
    console.log('Error caught in error boundary', args);

    // Defer all state updates to avoid React 19 setState-in-render errors
    setTimeout(() => {
      // render error notification in the case of runtime errors
      let stack = '';
      for (let index = 0; index < args.length; index++) {
        const element = args[index];
        if (element.componentStack) {
          stack += element.componentStack;
        } else {
          stack += element;
        }
      }
      const { createErrorStackTraceAction, locale } = this.props;
      createErrorStackTraceAction({ locale, response: stack });
      toast.error(<ApiErrorNotification />, {
        className: 'Toastify__toast Toastify__toast__no-hover',
        autoClose: false,
        closeOnClick: false,
        closeButton: <CloseToastButton />,
        draggable: false,
        toastId: API_ERROR_NOTIFICATION_ID,
      });
    }, 0);
  }

  componentDidUpdate() {
    const {
      dependencies,
      createErrorStackTraceAction,
      createCatchedErrorJsonAction,
      locale,
    } = this.props;
    const { processedDependencyErrors } = this.state;

    // Check if there are new dependency errors that haven't been processed yet
    const currentErrors = dependencies?.filter(({ error }) => error) || [];

    currentErrors.forEach((dep) => {
      // Use a unique identifier for each error to prevent reprocessing
      const errorId = dep.error?.message || JSON.stringify(dep.error);

      if (!processedDependencyErrors.has(errorId)) {
        // Mark this error as processed
        this.setState((prevState) => ({
          processedDependencyErrors: new Set([
            ...prevState.processedDependencyErrors,
            errorId,
          ]),
        }));

        // Process the error
        let browserStack = new Error().stack;
        try {
          sourceStackTrace.mapStackTrace(browserStack, (stackTrace) => {
            browserStack = stackTrace.join('\n');
          });

          const catchedError = dep.error;

          // Make error properties enumerable
          if (
            catchedError.message &&
            !{}.propertyIsEnumerable.call(catchedError, 'message')
          ) {
            Object.defineProperty(catchedError, 'message', {
              enumerable: true,
              value: catchedError.message,
            });
          }
          if (
            catchedError.stack &&
            !{}.propertyIsEnumerable.call(catchedError, 'stack')
          ) {
            Object.defineProperty(catchedError, 'stack', {
              enumerable: true,
              value: catchedError.stack,
            });
          }

          // Map stack trace
          if (catchedError.stack) {
            sourceStackTrace.mapStackTrace(catchedError.stack, (catchedStack) =>
              Object.defineProperty(catchedError, 'stack', {
                enumerable: true,
                value: catchedStack.join('\n'),
              }),
            );
          }

          // Dispatch actions without setTimeout since we're in componentDidUpdate
          createErrorStackTraceAction({ locale, response: browserStack });
          createCatchedErrorJsonAction({
            locale,
            response: JSON.stringify(catchedError, null, 2).replaceAll(
              '\\n',
              '\n',
            ),
          });
        } catch (e) {
          createErrorStackTraceAction({ locale, response: browserStack });
        }

        // Show toast notification if feature is disabled
        if (!window?.TIEOUT?.ENV?.FEATURE?.ENABLE_UNHANDLED_API_NOTIFCATION) {
          toast.error(<ApiErrorNotification />, {
            className: 'Toastify__toast Toastify__toast__no-hover',
            autoClose: false,
            closeOnClick: false,
            closeButton: <CloseToastButton />,
            draggable: false,
            toastId: API_ERROR_NOTIFICATION_ID,
          });
        }
      }
    });
  }

  render() {
    const {
      children,
      dependencies,
      errorComponent: ErrorComponent = () => (
        <div>
          <FormattedMessage id={'error-message'} />
        </div>
      ),
      loadingComponent: LoadingComponent = () => <Loading />,
    } = this.props;

    const { error } = this.state;

    // Check for errors (either from error boundary or dependencies)
    if (error || dependencies?.some(({ error }) => error)) {
      return <ErrorComponent />;
    }

    if (dependencies?.some(({ isLoading }) => isLoading)) {
      return <LoadingComponent />;
    }

    if (!dependencies?.every(({ isLoaded }) => isLoaded)) {
      return null;
    }

    return children;
  }
}

ConditionalRender.propTypes = {
  /** Node to be condtionaly rendered inside of the condition render component */
  children: PropTypes.node,
  /** Array of Api Models passed in that indicate the state of the api call and its returned data */
  dependencies: PropTypes.arrayOf(ApiModel),
  /** React component to be displayed for loading state */
  loadingComponent: PropTypes.element,
  /** React component to be displayed for error state */
  errorComponent: PropTypes.element,
  /** Function to create error stack trace action */
  createErrorStackTraceAction: PropTypes.func.isRequired,
  /** Function to create catched error JSON action */
  createCatchedErrorJsonAction: PropTypes.func.isRequired,
  /** Current locale */
  locale: PropTypes.string.isRequired,
};

const mapStateToProps = ({ ui: { locale } }) => ({
  locale,
});

const mapDispatchToProps = {
  createErrorStackTraceAction,
  createCatchedErrorJsonAction,
};

export default connect(mapStateToProps, mapDispatchToProps)(ConditionalRender);
